import EmptyCartImage from '@assets/icons/empty-cart-image.svg';
import i18n from '@/utils/i18n';
import React from 'react';
import {
  AvailabilityMap,
  ItemListOption,
  ItemListOptionMetadata,
} from '@/contexts/item-context-types';
import { CatalogItemListLoading } from './catalog-search/catalog-item-list-loading';
import { createStyles } from '@/contexts/theme-context';
import { EmptyStateMinimal } from './empty-state-minimal';
import { Props as ListItemProps } from './list-item';
import {
  RenderActionsWithSourceFn,
  SupplierCatalogList,
} from './orders/supplier-catalog-list';
import { SCREEN_PADDING } from '@/styles';
import { ScrollViewProps, StyleProp, View, ViewStyle } from 'react-native';
import {
  SEARCH_SORT_ORDERS,
  SearchSortOrder,
  useUserContext,
} from '@/contexts/user-context';
import {
  SECTION_HEADER_HEIGHT,
  SortOrderSectionHeader,
} from './orders/sort-order-section-header';

interface Props {
  availabilityMap?: AvailabilityMap;
  changeSortOrder?: (sortOrder: SearchSortOrder) => void;
  enforceSingleColumn?: boolean;
  isInboundOrder?: boolean;
  isModal?: boolean;
  items?: ItemListOption[] | null;
  listItemProps?: Partial<ListItemProps<ItemListOption>>;
  listItemSeparatorStyle?: StyleProp<ViewStyle>;
  loading?: boolean;
  loadingMore?: boolean;
  onEndReached?: () => void;
  renderActions?: RenderActionsWithSourceFn;
  renderNote?: (skuMetadata: ItemListOptionMetadata) => React.ReactNode;
  renderSource: string;
  scrollViewProps?: ScrollViewProps;
  searchSortOrder?: SearchSortOrder;
  searchText?: string;
  showVariantPackOrderInfo?: boolean;
  skipListInteractionCheck?: boolean;
}

export const SearchCatalogContent: React.FC<Props> = ({
  availabilityMap,
  enforceSingleColumn,
  isModal,
  items,
  listItemProps,
  listItemSeparatorStyle,
  loading,
  loadingMore,
  searchText,
  changeSortOrder,
  renderActions,
  renderNote,
  renderSource,
  scrollViewProps,
  searchSortOrder,
  showVariantPackOrderInfo,
  isInboundOrder,
  onEndReached,
  skipListInteractionCheck = false,
}) => {
  const [styles] = useStyles();
  const { isGuestMode } = useUserContext();

  const renderEmptyState = Array.isArray(items) && items.length === 0;
  return (
    <View style={styles.content}>
      {loading ? (
        <View
          style={[
            styles.loadingContainer,
            isModal && styles.loadingContainerModal,
          ]}
        >
          <CatalogItemListLoading
            enforceSingleColumn={enforceSingleColumn}
            isModal={isModal}
          />
        </View>
      ) : renderEmptyState ? (
        <EmptyStateMinimal
          containerStyle={styles.emptyStateContainer}
          ImageComponent={EmptyCartImage}
          text={i18n.t('no_search_results')}
        />
      ) : (
        <>
          <SupplierCatalogList
            availabilityMap={availabilityMap}
            enforceSingleColumn={enforceSingleColumn}
            isInboundOrder={isInboundOrder}
            items={items || []}
            listContentContainerStyle={
              isGuestMode || !searchSortOrder
                ? null
                : styles.listContentContainer
            }
            listItemProps={listItemProps}
            listItemSeparatorStyle={listItemSeparatorStyle}
            loadingMore={loadingMore}
            onEndReached={onEndReached}
            renderActions={renderActions}
            renderNote={renderNote}
            renderSource={renderSource}
            scrollViewProps={scrollViewProps}
            searchText={searchText}
            showSingleOption={false}
            showVariantPackOrderInfo={showVariantPackOrderInfo}
            skipListInteractionCheck={skipListInteractionCheck}
          />
          {items?.length && changeSortOrder && searchSortOrder ? (
            <View style={styles.sortOrderHeaderContainer}>
              <SortOrderSectionHeader<SearchSortOrder>
                onChange={changeSortOrder}
                supportedSortOptions={SEARCH_SORT_ORDERS}
                value={searchSortOrder}
              />
            </View>
          ) : null}
        </>
      )}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  content: {
    flex: 1,
    flexDirection: 'column-reverse',
  },
  emptyStateContainer: {
    backgroundColor: theme.colors.transparent,
  },
  listContentContainer: {
    paddingTop: SECTION_HEADER_HEIGHT,
  },
  loadingContainer: {
    flex: 1,
    marginTop: SCREEN_PADDING,
  },
  loadingContainerModal: {
    marginTop: 70,
  },
  // Absolute position the header so it floats above the catalog list
  // Adjust left and right positions to account for scroll bars
  sortOrderHeaderContainer: {
    alignSelf: 'center',
    position: 'absolute',
    top: 0,
  },
}));
